// Product, category and banner images through the CDN (Bunny, cdn.supergallery.nl).
//
// Set REACT_APP_IMAGE_BASE=https://cdn.supergallery.nl at build time to enable it. When it is empty
// (the default) every function returns the original URL, so the site behaves exactly as before.
// The CDN pulls from www.supergallery.nl, resizes with ?width= and serves WebP/AVIF based on the
// browser's Accept header. Widths are capped at MAX_WIDTH; the CDN also blocks larger requests.

const BASE = (process.env.REACT_APP_IMAGE_BASE || "").replace(/\/+$/, "");

// Hosts whose image URLs may be rewritten. doka serves the same image paths as production, so the
// CDN (origin: www) can serve them; other hosts (e.g. localhost) are rewritten only when same-origin.
const ORIGIN_HOSTS = ["www.supergallery.nl", "supergallery.nl", "doka.supergallery.nl"];

// Only image paths that exist on the origin go through the CDN:
// /supergallery/img/..., friendly product images /supergallery/123-large_default/name.jpg,
// module images /supergallery/modules/<module>/views/img/... and root /images/...
const CDN_PATH = /^\/(supergallery\/(img\/|\d+(-[a-z0-9_]+)?\/|modules\/[^/]+\/views\/img\/)|images\/)/i;

export const MAX_WIDTH = 2000;

export const cdnEnabled = Boolean(BASE);

function toCdnUrl(src) {
  if (!BASE || !src || typeof src !== "string") return null;
  let url;
  try {
    url = new URL(src, window.location.origin);
  } catch (e) {
    return null;
  }
  const sameOrigin = url.origin === window.location.origin;
  if (!sameOrigin && !ORIGIN_HOSTS.includes(url.hostname)) return null;
  if (!CDN_PATH.test(url.pathname)) return null;
  return new URL(url.pathname, BASE + "/");
}

/** Rewrites an image URL to the CDN with optional transforms; returns src unchanged when not applicable. */
export function cdnImage(src, { width, height, quality, version } = {}) {
  const url = toCdnUrl(src);
  if (!url) return src;
  if (width) url.searchParams.set("width", String(Math.min(Math.round(width), MAX_WIDTH)));
  if (height) url.searchParams.set("height", String(Math.round(height)));
  if (quality) url.searchParams.set("quality", String(quality));
  if (version) url.searchParams.set("v", String(version));
  return url.toString();
}

/** srcset for a list of widths, or undefined when the CDN does not apply (keeps the plain src). */
export function cdnSrcSet(src, widths, opts = {}) {
  if (!toCdnUrl(src)) return undefined;
  return [...new Set(widths.map((w) => Math.min(w, MAX_WIDTH)))]
    .map((w) => `${cdnImage(src, { ...opts, width: w })} ${w}w`)
    .join(", ");
}
